import React, { useEffect, useMemo } from "react";
import { Box, CircularProgress, Divider, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CommentItem, { CommentComposer } from "./CommentItem";
import DeleteConfirmation from "../../../../../../views/components/common/pop-up/DeleteConfirmation";
import ImageAPI from "../../../../api/ImageAPI";

const ATTACHMENT_CONTAINER = "task-comments";

const uploadAttachments = async (files: File[]): Promise<string[]> => {
  if (!files || files.length === 0) return [];
  return new ImageAPI().uploadImage(files, ATTACHMENT_CONTAINER);
};

interface TaskActivityPanelProps {
  taskId: number;
}

const TaskActivityPanel: React.FC<TaskActivityPanelProps> = ({ taskId }) => {
  const theme = useTheme();
  const { TaskTrackerStore, userStore } = useStores();
  const { showError } = useNotification();

  const { submitting, deleteTargetId } = TaskTrackerStore;

  const currentUserId = userStore.user?.id;

  useEffect(() => {
    if (!taskId) return;
    TaskTrackerStore.getTaskComments(taskId).catch((err) =>
      showError(
        "Error",
        err?.response?.data?.message || "Failed to load comments."
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const comments = useMemo(
    () => TaskTrackerStore.taskComments || [],
    [TaskTrackerStore.taskComments]
  );

  const withSubmitting = async (fn: () => Promise<void>, errMsg: string) => {
    try {
      TaskTrackerStore.setSubmitting(true);
      await fn();
    } catch (err: any) {
      showError("Error", err?.response?.data?.message || errMsg);
    } finally {
      TaskTrackerStore.setSubmitting(false);
    }
  };

  const handleAddComment = (message: string, files: File[]) =>
    withSubmitting(async () => {
      const urls = await uploadAttachments(files);
      await TaskTrackerStore.addTaskComment(
        taskId,
        message,
        undefined,
        urls,
        currentUserId
          ? { id: currentUserId, name: userStore.user?.name }
          : undefined
      );
    }, "Failed to add comment.");

  const handleEdit = (id: number, message: string) =>
    withSubmitting(
      () => TaskTrackerStore.editTaskComment(taskId, id, message),
      "Failed to edit comment."
    );

  const handleDelete = (id: number) => TaskTrackerStore.setDeleteTargetId(id);

  const handleConfirmDelete = (id: number) => {
    TaskTrackerStore.setDeleteTargetId(null);
    withSubmitting(
      () => TaskTrackerStore.deleteTaskComment(taskId, id),
      "Failed to delete comment."
    );
  };

  return (
    <Box>
      <CustomTypography variant="heading5" style={{ marginBottom: 8 }}>
        Comments
      </CustomTypography>
      <Divider sx={{ mb: 2 }} />

      <Box sx={{ mb: 2 }}>
        <CommentComposer
          showAvatar
          avatarName={userStore.user?.name}
          placeholder="Add a comment..."
          submitLabel="Comment"
          busy={submitting}
          onSubmit={handleAddComment}
        />
      </Box>

      {TaskTrackerStore.loadingComments && comments.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : comments.length === 0 ? (
        <CustomTypography
          variant="body2"
          color={theme.palette.customColors?.greyTextColor}
          style={{ paddingTop: 8, paddingBottom: 8 }}
        >
          No comments yet. Be the first to comment.
        </CustomTypography>
      ) : (
        comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            currentUserId={currentUserId}
            busy={submitting}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))
      )}

      <DeleteConfirmation
        id={deleteTargetId ?? 0}
        open={deleteTargetId !== null}
        title="Delete comment"
        content="Are you sure you want to delete this comment? This action cannot be undone."
        onDeleteConfirm={handleConfirmDelete}
        onCancel={() => TaskTrackerStore.setDeleteTargetId(null)}
      />
    </Box>
  );
};

export default observer(TaskActivityPanel);
